// Guided post-signup onboarding — the web counterpart of the iOS flow's "About
// you" + "Done" steps. (Welcome = the landing page; age-gate + legal + account
// creation happen on the register page.) Country + UI language are auto-detected
// from the browser and saved silently; everything here is optional/skippable.
import { useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth-context";
import { saveOnboardingProfile } from "@/api/profile";
import { resendVerification } from "@/api/auth";
import { UI_LANGUAGE_CODES } from "@/i18n/languages";
import { Alert, Button, Input, Spinner } from "@/components/ui";

const SUBJECTS = [
  "Math", "Chemistry", "History", "Programming", "Languages", "Art",
  "Music", "Science", "Literature", "Business", "Medicine", "Law",
];
const MAX_SUBJECTS = 5;
const DIFFICULTIES: { value: string; label: string; blurb: string }[] = [
  { value: "easy", label: "Easy", blurb: "Gentle — build confidence first." },
  { value: "medium", label: "Medium", blurb: "Balanced — the default." },
  { value: "hard", label: "Hard", blurb: "Challenging — push yourself." },
];

/** Best-effort browser locale → { language (supported or "en"), country (ISO-2) }. */
function detectLocale(): { language: string; country?: string } {
  const tag = navigator.language || "en";
  const [langRaw, regionRaw] = tag.split("-");
  const lang = (langRaw || "en").toLowerCase();
  const language = UI_LANGUAGE_CODES.includes(lang) ? lang : "en";
  const country = regionRaw && regionRaw.length === 2 ? regionRaw.toUpperCase() : undefined;
  return { language, country };
}

export function OnboardingPage() {
  const { t } = useTranslation();
  const { status, needsOnboarding, user, markOnboarded } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<"about" | "done">("about");
  const [learningStatus, setLearningStatus] = useState("");
  const [subjects, setSubjects] = useState<string[]>([]);
  const [difficulty, setDifficulty] = useState("medium");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resend, setResend] = useState<"idle" | "sending" | "sent">("idle");

  const locale = useMemo(detectLocale, []);

  if (status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center text-brand-600">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }
  if (status === "unauthenticated") return <Navigate to="/login" replace />;
  // Already onboarded (e.g. an existing iOS user) — don't trap them here.
  if (!needsOnboarding && step === "about") return <Navigate to="/app" replace />;

  const toggleSubject = (s: string) => {
    setSubjects((cur) =>
      cur.includes(s) ? cur.filter((x) => x !== s) : cur.length < MAX_SUBJECTS ? [...cur, s] : cur,
    );
  };

  const saveAndContinue = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveOnboardingProfile({
        learning_status: learningStatus.trim() || null,
        subject_interests: subjects,
        difficulty_preference: difficulty,
        preferred_language: locale.language,
        country: locale.country ?? null,
      });
      setStep("done");
    } catch (err) {
      setError(err instanceof Error ? err.message : t("Something went wrong. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const finish = () => {
    markOnboarded();
    navigate("/app", { replace: true });
  };

  const onResend = async () => {
    setResend("sending");
    try {
      await resendVerification();
      setResend("sent");
    } catch {
      setResend("idle");
    }
  };

  return (
    <div className="mx-auto flex min-h-screen max-w-lg flex-col px-5 py-10">
      <div className="mb-8 h-1.5 w-full overflow-hidden rounded-full bg-black/10">
        <div
          className="h-full rounded-full bg-brand-600 transition-all"
          style={{ width: step === "about" ? "60%" : "100%" }}
        />
      </div>

      {step === "about" ? (
        <div className="animate-fade-up space-y-7">
          <header>
            <h1 className="text-2xl font-bold tracking-tight">{t("Tell us about you")}</h1>
            <p className="mt-1 text-sm text-ink-faint">
              {t("This calibrates your questions. You can change all of it later in Settings.")}
            </p>
          </header>

          <section className="space-y-2">
            <Input
              label={t("What are you learning right now?")}
              value={learningStatus}
              onChange={(e) => setLearningStatus(e.target.value)}
              placeholder={t("e.g. 10th-grade biology")}
            />
          </section>

          <section className="space-y-2">
            <span className="block text-sm font-medium text-ink-soft">
              {t("Subjects you care about")}{" "}
              <span className="text-ink-faint">({subjects.length}/{MAX_SUBJECTS})</span>
            </span>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {SUBJECTS.map((s) => {
                const selected = subjects.includes(s);
                const atCap = !selected && subjects.length >= MAX_SUBJECTS;
                return (
                  <button
                    key={s}
                    type="button"
                    disabled={atCap}
                    onClick={() => toggleSubject(s)}
                    className={[
                      "rounded-xl border px-3 py-2 text-sm font-medium transition",
                      selected
                        ? "border-brand-600 bg-brand-600 text-white"
                        : "border-black/10 bg-white text-ink hover:border-brand-300",
                      atCap ? "opacity-40" : "",
                    ].join(" ")}
                  >
                    {t(s)}
                  </button>
                );
              })}
            </div>
          </section>

          <section className="space-y-2">
            <span className="block text-sm font-medium text-ink-soft">
              {t("How hard should questions be?")}
            </span>
            <div className="space-y-2">
              {DIFFICULTIES.map((d) => (
                <button
                  key={d.value}
                  type="button"
                  onClick={() => setDifficulty(d.value)}
                  className={[
                    "flex w-full items-start gap-3 rounded-xl border px-4 py-3 text-left transition",
                    difficulty === d.value
                      ? "border-brand-600 ring-1 ring-brand-600"
                      : "border-black/10 hover:border-brand-300",
                  ].join(" ")}
                >
                  <span
                    className={[
                      "mt-0.5 h-4 w-4 shrink-0 rounded-full border-2",
                      difficulty === d.value ? "border-brand-600 bg-brand-600" : "border-ink-faint",
                    ].join(" ")}
                  />
                  <span>
                    <span className="block text-sm font-semibold">{t(d.label)}</span>
                    <span className="block text-xs text-ink-faint">{t(d.blurb)}</span>
                  </span>
                </button>
              ))}
            </div>
          </section>

          {error && <Alert>{error}</Alert>}

          <div className="space-y-3">
            <Button size="lg" className="w-full" loading={saving} onClick={saveAndContinue}>
              {t("Continue")}
            </Button>
            <button
              type="button"
              onClick={() => setStep("done")}
              className="block w-full text-center text-sm text-ink-faint hover:text-ink"
            >
              {t("Skip for now")}
            </button>
          </div>
        </div>
      ) : (
        <div className="animate-fade-up flex flex-1 flex-col items-center justify-center text-center">
          <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-green-100 text-3xl">
            ✓
          </div>
          <h1 className="text-2xl font-bold tracking-tight">{t("You're set up!")}</h1>
          <p className="mt-2 max-w-sm text-sm text-ink-faint">
            {t("EduPause is ready. Generate a set and start turning study time into screen time.")}
          </p>

          {user && !user.email_verified && (
            <div className="mt-6 w-full rounded-xl border border-amber-200 bg-amber-50 p-4 text-left text-amber-900">
              <p className="text-sm font-medium">{t("Verify your email")}</p>
              <p className="mt-1 text-xs text-amber-800/80">
                {t("We sent a verification link to {{email}}. Tap it to confirm your address.", {
                  email: user.email,
                })}
              </p>
              <div className="mt-2">
                {resend === "sent" ? (
                  <span className="text-xs font-semibold text-green-700">{t("Link sent.")}</span>
                ) : (
                  <button
                    type="button"
                    onClick={onResend}
                    disabled={resend === "sending"}
                    className="text-xs font-semibold underline underline-offset-2 disabled:opacity-50"
                  >
                    {resend === "sending" ? t("Sending…") : t("Resend link")}
                  </button>
                )}
              </div>
            </div>
          )}

          <Button size="lg" className="mt-8 w-full" onClick={finish}>
            {t("Start using EduPause")}
          </Button>
        </div>
      )}
    </div>
  );
}
